import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  IconButton,
  Alert,
  CircularProgress,
  Skeleton,
  Chip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

import { getDeletedFiles } from "../../services/apiService";
import { formatDate, formatSize } from "./shared/formatters";

const POPPINS = { fontFamily: "'Poppins', sans-serif" };
const HEADER_COLOR = "#1B2559";
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const TableSkeleton = ({ rows = 6, cols = 5 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <TableRow key={i}>
        {Array.from({ length: cols }).map((__, j) => (
          <TableCell key={j}>
            <Skeleton variant="text" animation="wave" />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

/**
 * DeletedFilesLog — audit table of files that were removed from SharePoint
 * and soft-deleted during the daily reconciliation scan.
 */
const DeletedFilesLog = () => {
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchFiles = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError(null);
      try {
        const res = await getDeletedFiles({
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        });
        if (res?.error) {
          setError(res.message || "Failed to load deleted files.");
          return;
        }
        const rows = res?.data ?? [];
        setFiles(Array.isArray(rows) ? rows : []);
        setTotal(res?.total ?? rows.length);
      } catch {
        setError("An unexpected error occurred.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, rowsPerPage]
  );

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  return (
    <Box sx={{ p: 2 }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            variant="h6"
            sx={{ ...POPPINS, fontWeight: 700, color: HEADER_COLOR }}
          >
            Deleted Files Log
          </Typography>
          {total > 0 && (
            <Chip
              label={`${total} record${total !== 1 ? "s" : ""}`}
              size="small"
              color="default"
              variant="outlined"
              sx={{ ...POPPINS, fontSize: "0.7rem" }}
            />
          )}
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton
              size="small"
              onClick={() => fetchFiles(true)}
              disabled={refreshing}
              sx={{ color: "#1976d2" }}
            >
              {refreshing ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, ...POPPINS }}>{error}</Alert>}

      <Paper elevation={1} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {["File Name", "Uploader", "Size", "Detected Deleted", "Library"].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      ...POPPINS,
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      color: HEADER_COLOR,
                      backgroundColor: "#f5f7fb",
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableSkeleton rows={rowsPerPage} cols={5} />
              ) : files.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                        color: "text.secondary",
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 36, color: "#c8c8c8" }} />
                      <Typography variant="body2" sx={POPPINS}>
                        No deleted files detected yet.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                files.map((file) => (
                  <TableRow
                    key={file.item_id}
                    hover
                    sx={{ opacity: 0.75, "&:last-child td": { border: 0 } }}
                  >
                    {/* Name */}
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <InsertDriveFileOutlinedIcon
                          sx={{ color: "#9e9e9e", fontSize: 18, flexShrink: 0 }}
                        />
                        <Tooltip title={file.name}>
                          <Typography
                            variant="body2"
                            sx={{
                              ...POPPINS,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: 220,
                              color: "#555",
                            }}
                          >
                            {file.name || "—"}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </TableCell>

                    {/* Uploader */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <PersonOutlineIcon sx={{ fontSize: 14, color: "#9e9e9e" }} />
                        <Typography variant="body2" sx={{ ...POPPINS, color: "#555" }}>
                          {file.email_id || file.user_id || "—"}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Size */}
                    <TableCell>
                      <Typography variant="body2" sx={{ ...POPPINS, color: "#555" }}>
                        {formatSize(file.size_bytes)}
                      </Typography>
                    </TableCell>

                    {/* Detected deleted */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ ...POPPINS, color: "#f44336", whiteSpace: "nowrap" }}
                      >
                        {formatDate(file.scanned_at)}
                      </Typography>
                    </TableCell>

                    {/* Library */}
                    <TableCell>
                      <Typography variant="body2" sx={{ ...POPPINS, color: "#555" }}>
                        {file.library || "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          sx={{ ...POPPINS, fontSize: "0.8rem", borderTop: "1px solid #e0e0e0" }}
        />
      </Paper>
    </Box>
  );
};

export default DeletedFilesLog;
